// ระบบค่าธรรมเนียมเหรียญสำหรับตลาดค้าส่ง

/**
 * ค่าธรรมเนียมสำหรับแต่ละ Feature
 */
export const MARKETPLACE_FEES = {
  // เปิดร้าน
  OPEN_STORE: {
    min: 500,
    max: 1000,
    default: 750,
    description: 'ค่าธรรมเนียมเปิดร้าน'
  },
  
  // ลงสินค้า
  LIST_PRODUCT: {
    min: 10,
    max: 50,
    default: 30,
    description: 'ค่าธรรมเนียมลงสินค้าต่อรายการ'
  },
  
  // ประมูล (ผู้ขาย)
  CREATE_AUCTION: {
    min: 100,
    max: 200,
    default: 150,
    description: 'ค่าธรรมเนียมเปิดประมูล'
  },
  
  // ประมูล (ผู้ซื้อ - ต่อการเสนอราคา)
  PLACE_BID: {
    min: 10,
    max: 20,
    default: 15,
    description: 'ค่าธรรมเนียมเสนอราคา (refundable ถ้าไม่ชนะหรือประมูลถูกยกเลิก)',
    refundable: true
  },
  
  // ค่าปรับเมื่อยกเลิกประมูล (เจ้าของประมูล)
  CANCEL_AUCTION_PENALTY: {
    min: 50,
    max: 200,
    default: 100,
    description: 'ค่าปรับเมื่อยกเลิกประมูล (หักจากเจ้าของประมูล)'
  },
  
  // โฆษณา
  ADVERTISE: {
    min: 200,
    max: 500,
    default: 350,
    description: 'ค่าธรรมเนียมโฆษณา'
  },
  
  // ประกาศหาสินค้า
  CREATE_BUYER_REQUEST: {
    min: 50,
    max: 100,
    default: 75,
    description: 'ค่าธรรมเนียมประกาศหาสินค้า'
  },
  
  // ค่าธรรมเนียมการซื้อขาย (ต่อออเดอร์)
  ORDER_FEE: {
    min: 20,
    max: 100,
    default: 50,
    description: 'ค่าธรรมเนียมการซื้อขาย (คำนวณตามมูลค่าออเดอร์)',
    percentage: true, // คำนวณเป็นเปอร์เซ็นต์
    percentageRate: 0.01 // 1% ของมูลค่าออเดอร์
  }
};

/**
 * ดึงค่าธรรมเนียมตาม Feature
 * @param {string} feature - ชื่อ feature
 * @returns {object} - ข้อมูลค่าธรรมเนียม
 */
export const getFee = (feature) => {
  return MARKETPLACE_FEES[feature] || null;
};

/**
 * คำนวณค่าธรรมเนียมออเดอร์
 * @param {number} orderTotal - มูลค่าออเดอร์
 * @returns {number} - ค่าธรรมเนียม
 */
export const calculateOrderFee = (orderTotal) => {
  const feeConfig = MARKETPLACE_FEES.ORDER_FEE;
  
  if (feeConfig.percentage) {
    // คำนวณเป็นเปอร์เซ็นต์
    const percentageFee = orderTotal * feeConfig.percentageRate;
    // ใช้ค่าสูงสุดระหว่างเปอร์เซ็นต์กับค่าต่ำสุด
    return Math.max(percentageFee, feeConfig.min);
  }
  
  return feeConfig.default;
};

/**
 * ตรวจสอบว่ามีเหรียญพอสำหรับ feature หรือไม่
 * @param {number} currentCoins - เหรียญที่มี
 * @param {string} feature - ชื่อ feature
 * @param {object} options - ตัวเลือกเพิ่มเติม (เช่น orderTotal สำหรับ ORDER_FEE)
 * @returns {object} - { hasEnough: boolean, required: number, shortage: number }
 */
export const checkCoinRequirement = (currentCoins, feature, options = {}) => {
  const feeConfig = getFee(feature);
  
  if (!feeConfig) {
    return {
      hasEnough: false,
      required: 0,
      shortage: 0,
      message: 'ไม่พบค่าธรรมเนียมสำหรับ feature นี้'
    };
  }
  
  let required = feeConfig.default;
  
  // คำนวณค่าธรรมเนียมพิเศษสำหรับ ORDER_FEE
  if (feature === 'ORDER_FEE' && options.orderTotal) {
    required = calculateOrderFee(options.orderTotal);
  }
  
  const shortage = Math.max(0, required - currentCoins);
  
  return {
    hasEnough: currentCoins >= required,
    required,
    shortage,
    message: currentCoins >= required 
      ? null 
      : `เหรียญไม่พอ! คุณมี ${currentCoins} เหรียญ แต่ต้องใช้ ${required} เหรียญ (ขาด ${shortage} เหรียญ)`
  };
};

/**
 * คำนวณค่าธรรมเนียมรวมสำหรับตะกร้า
 * @param {Array} cartItems - รายการสินค้าในตะกร้า
 * @returns {object} - { subtotal: number, fee: number, total: number }
 */
export const calculateCartFees = (cartItems) => {
  if (!cartItems || cartItems.length === 0) {
    return {
      subtotal: 0,
      fee: 0,
      total: 0
    };
  }
  
  // คำนวณยอดรวมสินค้า
  const subtotal = cartItems.reduce((sum, item) => {
    return sum + (item.price * item.quantity);
  }, 0);
  
  // คำนวณค่าธรรมเนียม
  const fee = calculateOrderFee(subtotal);
  
  // ยอดรวม
  const total = subtotal + fee;
  
  return {
    subtotal,
    fee,
    total
  };
};



